/**
 * AI Drum Pattern Generator
 * AI-powered drum pattern generation using GPT-5.2
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Drum,
  Sparkles,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Check,
  X,
  Loader2,
  Lightbulb,
  Download,
  ChevronDown,
  ChevronUp,
  Volume2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options with subgenres
const GENRES = [
  { id: 'hiphop', name: 'Hip-Hop', emoji: '🎤', subgenres: ['trap', 'boom-bap', 'lofi', 'drill'] },
  { id: 'rock', name: 'Rock', emoji: '🎸', subgenres: ['classic', 'punk', 'indie', 'alternative'] },
  { id: 'electronic', name: 'Electronic', emoji: '🎧', subgenres: ['house', 'techno', 'dubstep', 'future-bass'] },
  { id: 'pop', name: 'Pop', emoji: '🎵', subgenres: ['dance-pop', 'synth-pop', 'indie-pop'] },
  { id: 'jazz', name: 'Jazz', emoji: '🎷', subgenres: ['bebop', 'swing', 'fusion'] },
  { id: 'funk', name: 'Funk', emoji: '🕺', subgenres: ['classic-funk', 'disco', 'boogie'] },
  { id: 'latin', name: 'Latin', emoji: '💃', subgenres: ['reggaeton', 'salsa', 'bossa-nova'] },
  { id: 'dnb', name: 'D&B', emoji: '⚡', subgenres: ['liquid', 'neurofunk', 'jungle'] },
];

// Complexity levels
const COMPLEXITY = [
  { id: 'simple', name: 'Simple' },
  { id: 'moderate', name: 'Moderate' },
  { id: 'complex', name: 'Complex' },
];

// Feel options
const FEELS = [
  { id: 'straight', name: 'Straight' },
  { id: 'shuffle', name: 'Shuffle' },
  { id: 'swing', name: 'Swing' },
  { id: 'halftime', name: 'Half-Time' },
];

// Intensity levels
const INTENSITIES = [
  { id: 'soft', name: 'Soft' },
  { id: 'medium', name: 'Medium' },
  { id: 'hard', name: 'Hard' },
  { id: 'aggressive', name: 'Aggressive' },
];

// Drum element display config
const DRUM_ELEMENTS = [
  { id: 'kick', name: 'Kick', color: '#ef4444', midiNote: 36 },
  { id: 'snare', name: 'Snare', color: '#f97316', midiNote: 38 },
  { id: 'hihat', name: 'Hi-Hat', color: '#eab308', midiNote: 42 },
  { id: 'hihat_open', name: 'Open HH', color: '#84cc16', midiNote: 46 },
  { id: 'clap', name: 'Clap', color: '#22c55e', midiNote: 39 },
  { id: 'tom_high', name: 'Tom Hi', color: '#06b6d4', midiNote: 50 },
  { id: 'tom_mid', name: 'Tom Mid', color: '#3b82f6', midiNote: 47 },
  { id: 'tom_low', name: 'Tom Lo', color: '#8b5cf6', midiNote: 45 },
  { id: 'crash', name: 'Crash', color: '#d946ef', midiNote: 49 },
  { id: 'ride', name: 'Ride', color: '#ec4899', midiNote: 51 },
];

// Pattern grid cell
const PatternCell = ({ active, velocity, color, onClick }) => (
  <div
    onClick={onClick}
    className={`w-5 h-5 rounded-sm cursor-pointer transition-all border ${
      active
        ? 'border-white/30'
        : 'border-zinc-700 hover:border-zinc-500'
    }`}
    style={{
      backgroundColor: active ? color : 'rgba(39, 39, 42, 0.5)',
      opacity: active ? (velocity || 100) / 127 : 0.3,
    }}
  />
);

// Pattern row for one drum element
const PatternRow = ({ element, pattern, velocities, stepsPerBar, isPlaying, currentStep, onToggle }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 text-xs text-zinc-400 truncate">{element.name}</div>
      <div className="flex gap-px">
        {pattern.map((hit, i) => (
          <div key={i} className="relative">
            {i > 0 && i % stepsPerBar === 0 && (
              <div className="absolute -left-1 top-0 bottom-0 w-px bg-zinc-600" />
            )}
            <PatternCell
              active={hit === 1}
              velocity={velocities?.[i] || 100}
              color={element.color}
              onClick={() => onToggle?.(element.id, i)}
            />
            {isPlaying && currentStep === i && (
              <div className="absolute inset-0 border-2 border-white rounded-sm pointer-events-none" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const AIDrumPatternGenerator = ({
  isOpen,
  onClose,
  onInsertPattern, // Callback to insert pattern into DAW
  audioContext,
  bpm: projectBpm = 120,
}) => {
  // State
  const [genre, setGenre] = useState('hiphop');
  const [subgenre, setSubgenre] = useState('');
  const [tempo, setTempo] = useState(projectBpm);
  const [complexity, setComplexity] = useState('moderate');
  const [bars, setBars] = useState(2);
  const [feel, setFeel] = useState('straight');
  const [intensity, setIntensity] = useState('medium');
  const [includeFills, setIncludeFills] = useState(false);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [copied, setCopied] = useState(false);
  const [showSubgenres, setShowSubgenres] = useState(false);
  
  const playbackRef = useRef(null);
  const schedulerRef = useRef(null);
  const nextStepTimeRef = useRef(0);

  // Update tempo when project BPM changes
  useEffect(() => {
    setTempo(projectBpm);
  }, [projectBpm]);

  // Stop playback on unmount
  useEffect(() => {
    return () => {
      if (playbackRef.current) clearInterval(playbackRef.current);
      if (schedulerRef.current) clearTimeout(schedulerRef.current);
    };
  }, []);

  // Generate pattern
  const generatePattern = async () => {
    setIsGenerating(true);
    setResult(null);
    stopPlayback();
    
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/generate-drum-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genre,
          subgenre: subgenre || null,
          tempo,
          complexity,
          bars,
          swing: 0,
          feel,
          intensity,
          include_fills: includeFills,
          kit_style: 'hybrid',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResult(data);
      toast.success('Drum pattern generated!');
    } catch (err) {
      console.error('Error generating pattern:', err);
      toast.error(err.message || 'Failed to generate pattern');
    } finally {
      setIsGenerating(false);
    }
  };

  // Simple click sound for playback preview
  const playClick = useCallback((element) => {
    if (!audioContext) return;
    
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    // Different frequencies for different drums
    const freqMap = {
      kick: 60,
      snare: 200,
      hihat: 800,
      hihat_open: 700,
      clap: 300,
      tom_high: 250,
      tom_mid: 180,
      tom_low: 120,
      crash: 500,
      ride: 600,
    };
    
    osc.frequency.value = freqMap[element] || 200;
    osc.type = element === 'kick' ? 'sine' : element.includes('hihat') ? 'square' : 'triangle';
    
    gain.gain.value = 0.3;
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
    
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    osc.start();
    osc.stop(audioContext.currentTime + 0.1);
  }, [audioContext]);

  // Play pattern
  const playPattern = useCallback(() => {
    if (!result?.pattern?.pattern) return;
    
    setIsPlaying(true);
    setCurrentStep(0);
    
    const pattern = result.pattern.pattern;
    const stepsPerBar = result.steps_per_bar || 16;
    const totalSteps = result.total_steps || bars * stepsPerBar;
    const stepDuration = (60 / tempo) / 4; // 16th note duration in seconds
    
    let step = 0;
    
    const tick = () => {
      setCurrentStep(step);
      
      // Play sounds for current step
      DRUM_ELEMENTS.forEach(element => {
        if (pattern[element.id]?.[step] === 1) {
          playClick(element.id);
        }
      });
      
      step = (step + 1) % totalSteps;
      playbackRef.current = setTimeout(tick, stepDuration * 1000);
    };
    
    tick();
  }, [result, tempo, bars, playClick]);

  // Stop playback
  const stopPlayback = useCallback(() => {
    if (playbackRef.current) {
      clearTimeout(playbackRef.current);
      playbackRef.current = null;
    }
    setIsPlaying(false);
    setCurrentStep(-1);
  }, []);

  // Copy pattern as text
  const copyPattern = () => {
    if (!result?.pattern?.pattern) return;
    
    const pattern = result.pattern.pattern;
    const text = DRUM_ELEMENTS
      .filter(el => pattern[el.id]?.some(v => v === 1))
      .map(el => `${el.name}: ${pattern[el.id].map(v => v ? 'X' : '-').join('')}`)
      .join('\n');
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Pattern copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Insert into DAW
  const insertIntoDaw = () => {
    if (!result?.pattern?.pattern) return;
    
    onInsertPattern?.(result.pattern, {
      genre,
      subgenre,
      tempo,
      bars,
      feel,
    });
    
    toast.success('Pattern inserted into track!');
    onClose?.();
  };

  // Get current subgenres
  const currentSubgenres = GENRES.find(g => g.id === genre)?.subgenres || [];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="ai-drum-generator"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                <Drum className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">AI Drum Pattern Generator</h2>
                <p className="text-xs text-zinc-400">Powered by GPT-5.2</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
            {/* Settings Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Genre */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Genre</label>
                <div className="grid grid-cols-4 gap-1">
                  {GENRES.map(g => (
                    <button
                      key={g.id}
                      onClick={() => {
                        setGenre(g.id);
                        setSubgenre('');
                      }}
                      className={`p-2 rounded-lg text-center transition-all ${
                        genre === g.id
                          ? 'bg-orange-500/20 border border-orange-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={g.name}
                    >
                      <span className="text-lg">{g.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {GENRES.find(g => g.id === genre)?.name}</p>
                
                {/* Subgenre selector */}
                {currentSubgenres.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowSubgenres(!showSubgenres)}
                      className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                    >
                      {showSubgenres ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      Subgenre
                    </button>
                    {showSubgenres && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {currentSubgenres.map(sub => (
                          <button
                            key={sub}
                            onClick={() => setSubgenre(subgenre === sub ? '' : sub)}
                            className={`px-2 py-0.5 rounded text-xs transition-all ${
                              subgenre === sub
                                ? 'bg-orange-500 text-white'
                                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}
                          >
                            {sub}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Tempo & Bars */}
              <div className="space-y-3">
                {/* Tempo */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Tempo: {tempo} BPM</label>
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={tempo}
                    onChange={(e) => setTempo(parseInt(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-zinc-500">
                    <span>60</span>
                    <span>200</span>
                  </div>
                </div>
                
                {/* Bars */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Bars</label>
                  <div className="flex gap-1">
                    {[1, 2, 4, 8].map(b => (
                      <button
                        key={b}
                        onClick={() => setBars(b)}
                        className={`px-3 py-1 rounded text-sm flex-1 transition-all ${
                          bars === b
                            ? 'bg-orange-500 text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Second Row: Complexity, Feel, Intensity */}
            <div className="grid grid-cols-3 gap-4">
              {/* Complexity */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Complexity</label>
                <div className="flex gap-1">
                  {COMPLEXITY.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setComplexity(c.id)}
                      className={`px-2 py-1 rounded text-xs flex-1 transition-all ${
                        complexity === c.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Feel */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Feel</label>
                <div className="flex gap-1">
                  {FEELS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setFeel(f.id)}
                      className={`px-2 py-1 rounded text-xs flex-1 transition-all ${
                        feel === f.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Intensity */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Intensity</label>
                <div className="flex gap-1">
                  {INTENSITIES.map(i => (
                    <button
                      key={i.id}
                      onClick={() => setIntensity(i.id)}
                      className={`px-2 py-1 rounded text-xs flex-1 transition-all ${
                        intensity === i.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {i.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Include Fills Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-fills"
                checked={includeFills}
                onChange={(e) => setIncludeFills(e.target.checked)}
                className="w-4 h-4 accent-orange-500"
              />
              <label htmlFor="include-fills" className="text-sm text-zinc-300">Include drum fills</label>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={generatePattern}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-12"
              data-testid="generate-drums-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Creating beat...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Pattern
                </>
              )}
            </Button>
            
            {/* Results */}
            {result && result.pattern && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Pattern Grid */}
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white capitalize">{genre}</span>
                      {subgenre && <span className="text-xs text-zinc-500 px-2 py-0.5 bg-zinc-800 rounded">{subgenre}</span>}
                      <span className="text-xs text-zinc-500">{tempo} BPM • {bars} bars</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyPattern}
                        className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={isPlaying ? stopPlayback : playPattern}
                        className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                      >
                        {isPlaying ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Pattern Grid */}
                  <div className="space-y-1 overflow-x-auto pb-2">
                    {DRUM_ELEMENTS.filter(el => 
                      result.pattern.pattern?.[el.id]?.some(v => v === 1)
                    ).map(element => (
                      <PatternRow
                        key={element.id}
                        element={element}
                        pattern={result.pattern.pattern[element.id] || []}
                        velocities={result.pattern.velocities?.[element.id]}
                        stepsPerBar={result.steps_per_bar || 16}
                        isPlaying={isPlaying}
                        currentStep={currentStep}
                      />
                    ))}
                  </div>
                  
                  {/* Beat markers */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-16" />
                    <div className="flex gap-px">
                      {Array.from({ length: result.total_steps || bars * 16 }, (_, i) => (
                        <div
                          key={i}
                          className={`w-5 text-center text-[9px] ${
                            i % 4 === 0 ? 'text-zinc-400' : 'text-zinc-600'
                          }`}
                        >
                          {i % 4 === 0 ? Math.floor(i / 4) + 1 : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Genre Notes & Tips */}
                {(result.pattern.genre_notes || result.pattern.groove_tips) && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-orange-400 mt-0.5" />
                      <div className="space-y-2">
                        {result.pattern.genre_notes && (
                          <p className="text-sm text-zinc-300">{result.pattern.genre_notes}</p>
                        )}
                        {result.pattern.groove_tips && (
                          <p className="text-sm text-zinc-400">{result.pattern.groove_tips}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Recommended Sounds */}
                {result.pattern.recommended_sounds && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Volume2 className="w-4 h-4 text-orange-400" />
                      <span className="text-sm font-medium text-white">Recommended Sounds</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {Object.entries(result.pattern.recommended_sounds).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="bg-zinc-800/50 p-2 rounded">
                          <p className="text-zinc-500 capitalize">{key}</p>
                          <p className="text-zinc-300">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Variations */}
                {result.pattern.variations?.length > 0 && (
                  <div className="p-4 bg-zinc-800/30 rounded-xl">
                    <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-orange-400" />
                      Pattern Variations
                    </h4>
                    <div className="space-y-2">
                      {result.pattern.variations.map((v, i) => (
                        <div key={i} className="p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-sm font-medium text-orange-400">{v.name}</p>
                          <p className="text-xs text-zinc-400">{v.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={generatePattern}
                    variant="outline"
                    className="flex-1 border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={insertIntoDaw}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Insert into Track
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIDrumPatternGenerator;
